import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { config, isProductionEnvironment } from '@this/shared/dist/tools/config.js';
import { redactBrowserTelemetryItem } from '@this/shared/dist/tools/telemetry-redaction.js';
import { reactPlugin } from './global.js';
/**
 * Starts Application Insights for a frontend. No credential leaves through it: request and response
 * headers of fetch and XHR calls are never recorded, and every item is redacted before it is sent — the
 * URLs of page views and calls (a SAS signature, a token in the query or fragment, the tRPC input), the
 * exception messages, the custom properties, the operation name, and the path segment after each of
 * `secretPathPrefixes`, for routes that carry a token in the path.
 */
export function initInsights(role, secretPathPrefixes) {
    // Don't initialize twice
    if (window.appInsights) {
        return;
    }
    // Initialize Application Insights
    window.appInsights = new ApplicationInsights({
        config: {
            connectionString: config.applicationInsights.connectionString,
            extensions: [reactPlugin],
            enableRequestHeaderTracking: false,
            enableResponseHeaderTracking: false,
        },
    });
    // redact every item before anything else sees it — its tags, base data, custom properties and the
    // operation name the SDK took from the page path; an item whose redaction fails is dropped, as the SDK
    // would send it unredacted after a throwing initializer
    window.appInsights.addTelemetryInitializer((envelope) => {
        try {
            redactBrowserTelemetryItem(envelope, secretPathPrefixes);
            return true;
        }
        catch (_e) {
            return false;
        }
    });
    window.appInsights.addTelemetryInitializer((envelope) => {
        if (!envelope.tags) {
            return;
        }
        envelope.tags['ai.cloud.role'] = `app-${role}`;
    });
    // In production, drop successful dependency telemetry (auto-collected
    // AJAX/fetch calls) to cut ingestion cost. Only failed calls are retained.
    if (isProductionEnvironment()) {
        window.appInsights.addTelemetryInitializer((envelope) => {
            if (envelope.baseType === 'RemoteDependencyData') {
                return envelope.baseData?.success === false;
            }
            return true;
        });
    }
    window.appInsights.loadAppInsights();
    window.appInsights.trackPageView();
}
